import { clone, equals } from 'ramda';
import type { Location } from 'slate';
import { Editor, Element, Node, Path, Point, Range } from 'slate';

import type { AlineSelection, NodeMatch, TableCellElement, TableRowElement } from '../internal';
import {
  AlineTransforms,
  isParagraphElement,
  isRenderModelParagraph,
  isTabElement,
  isTableCellElement,
  isTableElement,
  isTableRowElement,
  Queries,
  SelEventSource,
} from '../internal';

/**
 * Note: Selection transforms don't require editingMode checks as any user in any editing mode can use these transforms.
 * The transforms, at most, only change the selection and don't alter the document structure.
 */
const SelectionTransforms = {
  /**
   * Collapse the selection to an edge.  Typically done using keyboard events (e.g., left arrow).
   *
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-392.
   */
  collapseSel: (
    editor: Editor,
    options: {
      edge?: 'anchor' | 'focus' | 'start' | 'end';
      eventSource?: SelEventSource;
    } = {}
  ): void => {
    const { edge = 'anchor', eventSource = SelEventSource.KEYBOARD } = options;
    const { selection } = editor;

    if (!selection || Range.isCollapsed(selection)) return;

    const selEdge = Queries.rangeEdgeDirectional({ edge, range: selection });
    const point = selEdge === 'start' ? Range.start(selection) : Range.end(selection);

    const selectablePoint = Queries.getSelectablePoint(editor, {
      point,
      reverse: selEdge === 'start',
    });

    return AlineTransforms.select(editor, selectablePoint, { eventSource });
  },

  /**
   * Extend an active grid (table cell rectangle) selection by stepping its focus cell one column or
   * row (Word behavior) rather than moving the focus point through cell content. No-op when no grid
   * selection is active, so callers can fall back to point-based extension.
   */
  extendGridSelection(
    editor: Editor,
    options: {
      direction: 'down' | 'left' | 'right' | 'up';
      eventSource?: SelEventSource;
    }
  ): void {
    const { direction, eventSource = SelEventSource.KEYBOARD } = options;

    const gridSelectionInfo = Queries.getGridSelectionInfo(editor);
    if (!gridSelectionInfo || !editor.selection) return;

    const { gridSelectionAnchorColumn } = editor.selection;
    const { anchorCellEntry, focusCellEntry, tableEntry } = gridSelectionInfo;
    const matrix = Queries.getTableGridMatrix(tableEntry);
    const focusRect = Queries.getTableCellOccupiedRect(editor, focusCellEntry);
    const columnCount = matrix.at(0)?.length ?? 0;

    /**
     * At the table's horizontal edge the focus wraps so the rectangle keeps growing (Word): Right
     * past the last column steps down a row; Left past the first continues from the last column of
     * the previous row.
     */
    const isRightWrap = direction === 'right' && focusRect.endColumnIndex + 1 >= columnCount;
    const isLeftWrap = direction === 'left' && focusRect.startColumnIndex - 1 < 0;

    const targetCoordinates = isRightWrap
      ? { columnIndex: focusRect.startColumnIndex, rowIndex: focusRect.endRowIndex + 1 }
      : isLeftWrap
        ? { columnIndex: columnCount - 1, rowIndex: focusRect.startRowIndex - 1 }
        : {
            down: { columnIndex: focusRect.startColumnIndex, rowIndex: focusRect.endRowIndex + 1 },
            left: {
              columnIndex: focusRect.startColumnIndex - 1,
              rowIndex: focusRect.startRowIndex,
            },
            right: { columnIndex: focusRect.endColumnIndex + 1, rowIndex: focusRect.startRowIndex },
            up: { columnIndex: focusRect.startColumnIndex, rowIndex: focusRect.startRowIndex - 1 },
          }[direction];

    const targetSlot = matrix[targetCoordinates.rowIndex]?.[targetCoordinates.columnIndex] ?? null;
    if (!targetSlot) {
      // A missing slot within the row range is a short-row gap (no-op); only past the first/last
      // row does the focus leave the table, where handleTableSelection snaps it to whole rows.
      const isBeyondTableRows =
        targetCoordinates.rowIndex < 0 || targetCoordinates.rowIndex >= matrix.length;
      if (!isBeyondTableRows) return;

      const [, tablePath] = tableEntry;
      const outsideFocusPoint = getFocusPointBeyondTable(editor, {
        direction: isRightWrap || direction === 'down' ? 'down' : 'up',
        tablePath,
        unit: isRightWrap || isLeftWrap ? 'character' : 'line',
      });
      if (outsideFocusPoint)
        AlineTransforms.setSelection(editor, { eventSource, props: { focus: outsideFocusPoint } });

      return;
    }

    const [, targetCellPath] = targetSlot.tableCellEntry;
    const [, anchorCellPath] = anchorCellEntry;

    if (!Path.equals(targetCellPath, anchorCellPath))
      return AlineTransforms.setSelection(editor, {
        eventSource,
        props: { focus: Editor.start(editor, targetCellPath), gridSelectionAnchorColumn },
      });

    // Stepping back onto the anchor cell selects its whole content, focus at the edge opposite the
    // anchor so the selection stays expanded.
    const anchorAtCellStart = Point.equals(
      editor.selection.anchor,
      Editor.start(editor, anchorCellPath)
    );
    AlineTransforms.setSelection(editor, {
      eventSource,
      props: {
        focus: anchorAtCellStart
          ? Editor.end(editor, anchorCellPath)
          : Editor.start(editor, anchorCellPath),
        gridSelectionAnchorColumn,
      },
    });
  },

  /**
   * Step the focus one row up or down for a selection whose focus is inside a table that the
   * selection also extends beyond — the cross-table state where handleTableSelection snaps the
   * in-table edge to whole rows, and point-based extension is either a no-op or swallows a whole row.
   * Returns whether it applied; false means the caller should fall back to point-based extension.
   */
  extendRowSelection(
    editor: Editor,
    options: {
      direction: 'down' | 'up';
      eventSource?: SelEventSource;
      unit?: 'character' | 'line';
    }
  ): boolean {
    const { direction, eventSource = SelEventSource.KEYBOARD, unit = 'character' } = options;
    const { selection } = editor;

    if (!selection || Range.isCollapsed(selection)) return false;
    if (Queries.getGridSelectionInfo(editor)) return false;

    const [focusTableEntry, anchorTableEntry] = [selection.focus, selection.anchor].map(point =>
      Editor.above(editor, { at: point, match: isTableElement, mode: 'highest' })
    );
    if (!focusTableEntry) return false;

    const [focusTable, focusTablePath] = focusTableEntry;
    // Within a single table, grid and text selection modes apply instead.
    if (anchorTableEntry && Path.equals(anchorTableEntry[1], focusTablePath)) return false;

    const focusRowEntry = Editor.above(editor, {
      at: selection.focus,
      match: isTableRowElement,
      mode: 'highest',
    });
    if (!focusRowEntry) return false;

    const [, focusRowPath] = focusRowEntry;
    const focusRowIndex = focusRowPath.at(-1) ?? 0;
    const targetRowIndex = direction === 'down' ? focusRowIndex + 1 : focusRowIndex - 1;

    // Past the first/last row the focus leaves the table and point-based extension resumes there.
    if (targetRowIndex < 0 || targetRowIndex >= focusTable.children.length) {
      const outsideFocusPoint = getFocusPointBeyondTable(editor, {
        direction,
        tablePath: focusTablePath,
        unit,
      });
      if (outsideFocusPoint)
        AlineTransforms.setSelection(editor, { eventSource, props: { focus: outsideFocusPoint } });

      return true;
    }

    // Any point in the target row works; handleTableSelection snaps the in-table edge to the row.
    const targetRowPath = [...focusTablePath, targetRowIndex];
    AlineTransforms.setSelection(editor, {
      eventSource,
      props: { focus: Editor.start(editor, targetRowPath) },
    });

    return true;
  },

  /**
   * Nudges the selection correctly to encompass table cells/rows.
   */
  handleTableSelection(
    editor: Editor,
    options: {
      eventSource: SelEventSource;
      selection: AlineSelection;
    }
  ): AlineSelection {
    const { eventSource, selection } = options;
    if (eventSource === SelEventSource.PROGRAMMATIC) return selection;

    const newSel = clone(selection);
    const [start, end] = Range.edges(newSel);
    const startKey = Queries.rangeEdge({ edge: 'start', range: newSel });
    const endKey = Queries.rangeEdge({ edge: 'end', range: newSel });
    const [startTableEntry, endTableEntry] = [start, end].map(point =>
      Editor.above(editor, { at: point, match: isTableElement, mode: 'highest' })
    );

    if (startTableEntry) {
      const [, startTablePath] = startTableEntry;
      const isEndSameTable = !!endTableEntry && Path.equals(startTablePath, endTableEntry[1]);
      if (!isEndSameTable) {
        const startRowEntry = Editor.above(editor, {
          at: start,
          match: isTableRowElement,
          mode: 'highest',
        });
        if (!startRowEntry) throw new Error('Expected start row.');

        const [, startRowPath] = startRowEntry;

        newSel[startKey] = Editor.start(editor, startRowPath);
      } else {
        /**
         * The start and end of the selection are w/in the same table.
         * Get the lowest cells containing the start and end of the selection.
         */
        const [startCellEntry, endCellEntry] = [start, end].map(point =>
          Editor.above(editor, {
            at: point,
            match: isTableCellElement,
            mode: 'lowest',
          })
        );
        if (!startCellEntry || !endCellEntry) throw new Error('Expected cells.');

        const [, startCellPath] = startCellEntry;
        const [, endCellPath] = endCellEntry;
        const commonPath = Path.common(startCellPath, endCellPath);
        const commonNode = Node.get(editor, commonPath);
        const isWithinSameCell = isTableCellElement(commonNode);
        if (isWithinSameCell) {
          /**
           * Get the highest row containing the start/end *where the row is a descendant of the relevant cell*.
           * Then nudge the selection to the start/end of the matching row, if any.
           */
          const [startRowEntry, endRowEntry] = [start, end].map(point =>
            Queries.matchAbove<TableRowElement>(editor, {
              at: point,
              match: (n, p) => isTableRowElement(n) && Path.isDescendant(p, commonPath),
              mode: 'highest',
            })
          );
          if (startRowEntry) {
            const [, startRowPath] = startRowEntry;
            newSel[startKey] = Editor.start(editor, startRowPath);
          }
          if (endRowEntry) {
            const [, endRowPath] = endRowEntry;
            newSel[endKey] = Editor.end(editor, endRowPath);
          }

          return newSel;
        }

        /**
         * Anchor and focus are in the same table but different cells. Resolve each to its highest
         * common-ancestor cell, then to that cell's vertical-merge restart. Two distinct logical
         * cells form a rectangular grid selection: the anchor point stays in the anchor cell and the
         * focus in the focus cell, and the rectangle is derived from those points by
         * getGridSelectionInfo (which is why the endpoints, not start/end, are snapped here).
         */
        const [highestAnchorCellEntry, highestFocusCellEntry] = [newSel.anchor, newSel.focus].map(
          point =>
            Queries.matchAbove<TableCellElement>(editor, {
              at: point,
              match: (n, p) => isTableCellElement(n) && Path.isDescendant(p, commonPath),
              mode: 'highest',
            })
        );
        if (!highestAnchorCellEntry || !highestFocusCellEntry) throw new Error('Expected cells.');

        const [, anchorCellPath] = Queries.getVerticallyMergedTableCellStart(
          editor,
          highestAnchorCellEntry
        );
        const [, focusCellPath] = Queries.getVerticallyMergedTableCellStart(
          editor,
          highestFocusCellEntry
        );

        // Both endpoints resolve to one logical cell (a restart cell and its ghost): text selection.
        if (Path.equals(anchorCellPath, focusCellPath)) {
          const isBackward = Range.isBackward(newSel);
          newSel.anchor = isBackward
            ? Editor.end(editor, anchorCellPath)
            : Editor.start(editor, anchorCellPath);
          newSel.focus = isBackward
            ? Editor.start(editor, anchorCellPath)
            : Editor.end(editor, anchorCellPath);

          return newSel;
        }

        const isForward = Path.isBefore(anchorCellPath, focusCellPath);
        newSel.anchor = isForward
          ? Editor.start(editor, anchorCellPath)
          : Editor.end(editor, anchorCellPath);
        newSel.focus = isForward
          ? Editor.end(editor, focusCellPath)
          : Editor.start(editor, focusCellPath);

        return newSel;
      }
    }

    // The end is w/in a table, but the table is different from the start table.
    if (endTableEntry) {
      const [, endTablePath] = endTableEntry;
      const isStartSameTable = !!startTableEntry && Path.equals(endTablePath, startTableEntry[1]);
      if (!isStartSameTable) {
        const endRowEntry = Editor.above(editor, {
          at: end,
          match: isTableRowElement,
          mode: 'highest',
        });
        if (!endRowEntry) throw new Error('Expected end row.');

        const [, endRowPath] = endRowEntry;

        newSel[endKey] = Editor.end(editor, endRowPath);
      }
    }

    return newSel;
  },

  /**
   * Moves the selection to the left or to the right.
   */
  moveSel: (
    editor: Editor,
    options: {
      distance?: number;
      edge?: 'both' | 'focus';
      eventSource?: SelEventSource;
      reverse?: boolean;
      /**
       * Whether extending at a table cell's edge first selects the whole cell before leaving it (as
       * in Word). Callers that extend the focus to compute a range (e.g. word deletion) opt out.
       */
      selectWholeCellAtEdge?: boolean;
      unit?: 'offset' | 'character' | 'word';
    } = {}
  ): void => {
    const { selection } = editor;
    if (!selection) return;

    const {
      distance = 1,
      edge = 'both',
      eventSource = SelEventSource.KEYBOARD,
      reverse = false,
      selectWholeCellAtEdge = true,
      unit = 'character',
    } = options;

    // Extending an active grid selection steps its focus cell, not the point through cell content.
    if (edge === 'focus') {
      if (Queries.getGridSelectionInfo(editor))
        return AlineTransforms.extendGridSelection(editor, {
          direction: reverse ? 'left' : 'right',
          eventSource,
        });

      // In the cross-table state (in-table edge snapped to whole rows), step one row instead.
      if (
        AlineTransforms.extendRowSelection(editor, {
          direction: reverse ? 'up' : 'down',
          eventSource,
        })
      )
        return;

      const focusCellEntry = Editor.above(editor, {
        at: selection.focus,
        match: isTableCellElement,
        mode: 'lowest',
      });
      const isSelectionWithinFocusCell =
        !!focusCellEntry && Path.isDescendant(selection.anchor.path, focusCellEntry[1]);

      if (focusCellEntry && isSelectionWithinFocusCell) {
        const [, focusCellPath] = focusCellEntry;
        const cellEdge = reverse
          ? Editor.start(editor, focusCellPath)
          : Editor.end(editor, focusCellPath);

        if (Point.equals(selection.focus, cellEdge)) {
          // Word: extending at a cell edge first selects the whole cell before crossing into the
          // next one. Opted-out callers (word deletion) stop here; and at the table's outer edge
          // there is no neighbouring cell, so fall through to leave the table directly.
          if (!selectWholeCellAtEdge) return;

          const cellOppositeEdge = reverse
            ? Editor.end(editor, focusCellPath)
            : Editor.start(editor, focusCellPath);
          const beyondEdge = reverse
            ? Editor.before(editor, cellEdge)
            : Editor.after(editor, cellEdge);
          const focusTableEntry = Editor.above(editor, {
            at: selection.focus,
            match: isTableElement,
            mode: 'lowest',
          });
          const hasAdjacentCell =
            !!beyondEdge &&
            !!focusTableEntry &&
            Path.isDescendant(beyondEdge.path, focusTableEntry[1]);
          const isWholeCellSelected = Point.equals(selection.anchor, cellOppositeEdge);

          if (hasAdjacentCell && !isWholeCellSelected)
            return AlineTransforms.setSelection(editor, {
              eventSource,
              props: { anchor: cellOppositeEdge, focus: cellEdge },
            });
        }
      }
    }

    const { anchor, focus } = selection;
    const opts = { distance, unit };
    const props: Partial<Range> = {};

    const baseNewFocus = reverse
      ? Editor.before(editor, focus, opts)
      : Editor.after(editor, focus, opts);

    if (baseNewFocus) {
      props.focus = Queries.getSelectablePoint(editor, {
        point: baseNewFocus,
        reverse,
      });
    }

    if (edge === 'both') {
      const baseNewAnchor = reverse
        ? Editor.before(editor, anchor, opts)
        : Editor.after(editor, anchor, opts);

      if (baseNewAnchor) {
        props.anchor = Queries.getSelectablePoint(editor, { point: baseNewAnchor, reverse });
      }
    }

    const newSelection = { ...selection, ...props };
    if (Range.isCollapsed(selection) && Range.isCollapsed(newSelection)) {
      const { anchor } = selection;
      const { focus } = newSelection;
      if (!Point.equals(anchor, focus))
        AlineTransforms.announce(editor, Queries.string(editor, { at: { anchor, focus } }));
    }

    AlineTransforms.setSelection(editor, { eventSource, props });
  },

  /**
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-392.
   */
  moveSelHorizontally: (
    editor: Editor,
    options: {
      direction: 'forward' | 'backward';
      eventSource?: SelEventSource;
      unit?: 'character' | 'word';
    }
  ): void => {
    const { direction, eventSource = SelEventSource.KEYBOARD, unit } = options;

    if (!editor.selection) return;

    if (Range.isCollapsed(editor.selection)) {
      if (direction === 'forward' && editor.selection.listMarker)
        return AlineTransforms.setSelection(editor, { eventSource, props: { listMarker: null } });

      return AlineTransforms.moveSel(editor, {
        eventSource,
        reverse: direction === 'backward',
        unit,
      });
    }

    AlineTransforms.collapseSel(editor, {
      edge: direction === 'backward' ? 'start' : 'end',
      eventSource,
    });
  },

  /**
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-392.
   */
  moveSelToLineEdge: (
    editor: Editor,
    edge: 'start' | 'end',
    options: { eventSource?: SelEventSource; point?: 'both' | 'focus' } = {}
  ): void => {
    const { eventSource = SelEventSource.KEYBOARD, point = 'both' } = options;
    if (!editor.selection) return;

    const rMLineEntry = Queries.getRMLinesAboveCMPoint(
      editor,
      editor.selection.focus,
      editor.selection.affinity ?? 'forward'
    ).next().value;
    if (!rMLineEntry) return;
    const [rMFocusLine, rMFocusLinePath] = rMLineEntry;

    const rMParaEntry = Queries.renderModelAbove(editor, {
      at: rMFocusLinePath,
      match: isRenderModelParagraph,
    });
    if (!rMParaEntry) return;
    const [, rMFocusParaPath] = rMParaEntry;

    const relativePoint =
      edge === 'start' ? rMFocusLine.relativeStartPoint : rMFocusLine.relativeEndPoint;
    const cMFocusParaPath = Queries.getCMPathFromRMPath(editor, rMFocusParaPath);
    const newPoint = Queries.absolutePoint(relativePoint, cMFocusParaPath);

    let anchor = editor.selection.anchor;
    let focus = editor.selection.focus;
    if (point === 'both') {
      anchor = newPoint;
    }
    if (point === 'both' || point === 'focus') {
      focus = newPoint;
    }

    AlineTransforms.setSelection(editor, {
      eventSource,
      props: {
        affinity: edge === 'start' ? 'forward' : 'backward',
        anchor,
        focus,
      },
    });
  },

  /**
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-392.
   */
  moveSelToParagraphEdge(
    editor: Editor,
    options: {
      direction: 'forward' | 'backward';
      edge: 'both' | 'focus';
      eventSource?: SelEventSource;
    }
  ): void {
    const { direction, edge, eventSource = SelEventSource.KEYBOARD } = options;
    if (!editor.selection) return;

    /**
     * Unhang the selection first if it's hanging. An extended focus is left as is: unhanging would
     * pull it back against the direction of travel, e.g. off the line after a table into the table.
     */
    if (edge === 'both')
      AlineTransforms.unhangRange(editor, { at: editor.selection, select: true });

    const paragraphEntry = Editor.above(editor, {
      at: editor.selection.focus,
      match: isParagraphElement,
    });
    if (!paragraphEntry) return;

    const [, paragraphPath] = paragraphEntry;
    const finder = direction === 'backward' ? Editor.start : Editor.end;
    const point = finder(editor, paragraphPath);
    const newSel = {
      anchor: edge === 'both' ? point : editor.selection.anchor,
      focus: point,
    };

    AlineTransforms.setSelection(editor, { eventSource, props: newSel });
  },

  /**
   * Used to nudge a Range (e.g., the selection) to the edges of a matching node(s).
   * Used, for example, with links when a user's selection partly includes other inlines (i.e., so we don't have links wrapping other inlines).
   * Also used with input fields to move the selection to the edges of the input field(s) before performing an operation (e.g., delete).
   */
  nudgeRangeToEdges: (editor: Editor, options: { at: Range; match?: NodeMatch }): Range => {
    const { at, match = node => Element.isElement(node) && Editor.isInline(editor, node) } =
      options;

    /**
     * We make a copy of the Range so that we don't inadvertently mutate editor.selection if it is passed in as a parameter.
     * Since Immer freezes objects in dev, mutating the selection directly can cause issues.
     */
    const range = { ...at };

    /**
     * First, we check whether the anchor or focus is inside a matching node (e.g., an inline).
     * If yes, we move the Range to the edges of the match(s) and set the selection correctly.
     */
    const [start, end] = Range.edges(range);
    const isForward = Range.isForward(range);
    const startKey = isForward ? 'anchor' : 'focus';
    const endKey = isForward ? 'focus' : 'anchor';

    const entryAboveStart = Editor.above(editor, {
      at: start,
      match,
    });

    if (entryAboveStart) {
      const [, startPath] = entryAboveStart;
      const startPoint = Editor.start(editor, startPath);
      range[startKey] = startPoint;
      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.PROGRAMMATIC,
        props: { [startKey]: startPoint },
      });
    }

    const entryAboveEnd = Editor.above(editor, {
      at: end,
      match,
    });

    if (entryAboveEnd) {
      const [, endPath] = entryAboveEnd;
      const endPoint = Editor.end(editor, endPath);
      range[endKey] = endPoint;
      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.PROGRAMMATIC,
        props: { [endKey]: endPoint },
      });
    }

    return range;
  },

  /**
   * Select a Location in the editor.
   */
  select: (
    editor: Editor,
    target: Location,
    options: { eventSource?: SelEventSource } = {}
  ): void => {
    const { eventSource = SelEventSource.PROGRAMMATIC } = options;
    const newSel = Editor.range(editor, target);

    AlineTransforms.setSelection(editor, { eventSource, props: newSel });
  },

  selectAll: (editor: Editor): void => {
    const entryAbove = Queries.selectionBoundaryAboveLoc(editor);

    if (!entryAbove) return;

    const [, pathAbove] = entryAbove;

    AlineTransforms.select(editor, pathAbove);
  },

  /**
   * Select the start or end of the document part the selection is currently within.
   *
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-392.
   */
  selectDocPartEdge: (
    editor: Editor,
    options: { edge: 'start' | 'end'; eventSource?: SelEventSource }
  ): void => {
    const { edge, eventSource = SelEventSource.KEYBOARD } = options;

    const entryAbove = Queries.selectionBoundaryAboveLoc(editor);

    if (!entryAbove) return;

    const [, pathAbove] = entryAbove;

    switch (edge) {
      case 'start': {
        const start = Editor.start(editor, pathAbove);
        return AlineTransforms.select(editor, start, { eventSource });
      }
      case 'end': {
        const end = Editor.end(editor, pathAbove);
        return AlineTransforms.select(editor, end, { eventSource });
      }
    }
  },

  /**
   * Set the selection's properties while also validating the new selection before it is applied.
   *
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-397.
   */
  setSelection: (
    editor: Editor,
    options: { eventSource: SelEventSource; props: Partial<AlineSelection> }
  ): void => {
    const { eventSource, props } = options;

    if (!editor.selection) {
      if (!Range.isRange(props)) {
        console.error(`Cannot set a null selection without a complete Range.`);

        return;
      }

      const newSel = normalizeGridSelectionProps(
        editor,
        AlineTransforms.handleTableSelection(editor, {
          eventSource,
          selection: { ...props, moveVertWidth: props.moveVertWidth ?? null },
        })
      );
      if (!Queries.newSelectionValid(editor, { eventSource, newSel })) return;

      editor.apply({
        newProperties: newSel,
        properties: null,
        type: 'set_selection',
      });

      return;
    }

    // Note: We intentionally omit affinity here so that it gets set to forward in Editor.transform when a set_selection op is applied.
    const { anchor, focus } = editor.selection;
    const newSel = normalizeGridSelectionProps(
      editor,
      AlineTransforms.handleTableSelection(editor, {
        eventSource,
        selection: { anchor, focus, moveVertWidth: props.moveVertWidth ?? null, ...props },
      })
    );

    if (
      !Queries.newSelectionValid(editor, { eventSource, newSel }) ||
      equals(editor.selection, newSel)
    )
      return;

    if (eventSource === SelEventSource.KEYBOARD) {
      const [deselectedRanges, selectedRanges] = excludeRanges(editor.selection, newSel);

      for (const deselectedRange of deselectedRanges) {
        AlineTransforms.announce(editor, Queries.string(editor, { at: deselectedRange }));
        AlineTransforms.announce(editor, 'Unselected');
      }

      for (const selectedRange of selectedRanges) {
        AlineTransforms.announce(editor, Queries.string(editor, { at: selectedRange }));
        AlineTransforms.announce(editor, 'Selected');
      }
    }

    editor.apply({
      newProperties: newSel,
      properties: clone(editor.selection),
      type: 'set_selection',
    });
  },

  /**
   * Changes a range from hanging to non-hanging.
   */
  unhangRange: (
    editor: Editor,
    options: { at: Range; select?: boolean; voids?: boolean }
  ): Range => {
    const { at, voids = false } = options;
    const atIsSelection = !!editor.selection && Range.equals(at, editor.selection);
    const select = (options.select ?? true) && atIsSelection;

    // The Range is not hanging if it's collapsed so we simply return what was passed in.
    if (Range.isCollapsed(at)) {
      if (select) AlineTransforms.select(editor, at);
      return at;
    }

    /**
     * We make a copy of the Range so that we don't inadvertently mutate editor.selection if it is passed in as the at parameter.
     * The range is mutated below.
     */
    const range = { ...at };
    const [start, end] = Range.edges(range);
    const isForward = Range.isForward(range);
    const startKey = isForward ? 'anchor' : 'focus';
    const endKey = isForward ? 'focus' : 'anchor';
    const startAtEnd = Editor.isEnd(editor, start, start.path);
    const endAtStart = Editor.isStart(editor, end, end.path);

    if (startAtEnd) {
      const nextSiblingPath = Path.next(start.path);
      const nextSibling = Node.getIf(editor, nextSiblingPath);
      const [parent] = Editor.parent(editor, start.path);

      /**
       * Do not unhang start if the start is inside an inline void element or the next sibling is one.
       * This allows proper formatting of inline void elements (tabs, input fields, instruction fields, etc.).
       */
      const nextIsInlineVoid =
        nextSibling &&
        Element.isElement(nextSibling) &&
        Editor.isInline(editor, nextSibling) &&
        editor.isVoid(nextSibling);
      const parentIsInlineVoid =
        Element.isElement(parent) && Editor.isInline(editor, parent) && editor.isVoid(parent);

      if (!nextIsInlineVoid && !parentIsInlineVoid) {
        const after = Editor.after(editor, start, { voids });

        if (after) {
          range[startKey] = after;

          if (select)
            AlineTransforms.setSelection(editor, {
              eventSource: SelEventSource.PROGRAMMATIC,
              props: { [startKey]: after },
            });
        }
      }
    }

    if (endAtStart) {
      const prevSiblingPath = Path.hasPrevious(end.path) ? Path.previous(end.path) : null;
      const prevSibling = prevSiblingPath && Node.getIf(editor, prevSiblingPath);

      const [parent] = Editor.parent(editor, end.path);

      /**
       * Do not unhang end if the end is inside an inline void element or the previous sibling is one.
       * This allows proper formatting of inline void elements (tabs, input fields, instruction fields, etc.).
       */
      const prevIsInlineVoid =
        prevSibling &&
        Element.isElement(prevSibling) &&
        Editor.isInline(editor, prevSibling) &&
        editor.isVoid(prevSibling);
      const parentIsInlineVoid =
        Element.isElement(parent) && Editor.isInline(editor, parent) && editor.isVoid(parent);

      if (!prevIsInlineVoid && !parentIsInlineVoid) {
        const before = Editor.before(editor, end, { voids });

        if (before) {
          range[endKey] = before;

          if (select)
            AlineTransforms.setSelection(editor, {
              eventSource: SelEventSource.PROGRAMMATIC,
              props: { [endKey]: before },
            });
        }
      }
    }

    return range;
  },

  /**
   * Changes a range from hanging to non-hanging. For cases when anchor/focus is inside/after/before tab
   */
  unhangRangeWithTab: (editor: Editor, at: Range): Range => {
    // The Range is not hanging if it's collapsed so we simply return what was passed in.
    if (Range.isCollapsed(at)) {
      return at;
    }

    /**
     * We make a copy of the Range so that we don't inadvertently mutate editor.selection if it is passed in as the at parameter.
     * The range is mutated below.
     */
    const range = { ...at };
    const [start, end] = Range.edges(range);
    const isForward = Range.isForward(range);
    const startKey = isForward ? 'anchor' : 'focus';
    const endKey = isForward ? 'focus' : 'anchor';
    const startAtEnd = Editor.isEnd(editor, start, start.path);
    const endAtStart = Editor.isStart(editor, end, end.path);

    if (startAtEnd) {
      const nextSiblingPath = Path.next(start.path);
      const nextSibling = Node.getIf(editor, nextSiblingPath);

      // Check if range starts with tab.
      if (nextSibling && isTabElement(nextSibling)) {
        const after = Editor.point(editor, nextSiblingPath);

        if (after) {
          range[startKey] = after;
        }
      }
    }

    if (endAtStart) {
      const prevSiblingPath = Path.hasPrevious(end.path) ? Path.previous(end.path) : null;
      const prevSibling = prevSiblingPath && Node.getIf(editor, prevSiblingPath);

      // Check if range ends with tab.
      if (prevSibling && isTabElement(prevSibling)) {
        const before = prevSiblingPath && Editor.point(editor, prevSiblingPath);

        if (before) {
          range[endKey] = before;
        }
      }
    }

    return range;
  },
};

/**
 * The focus point for a selection stepping out of a table. A character step stops just past the
 * table's boundary. A line step takes the far edge of the first line beyond it, so one press selects
 * that whole line instead of leaving the focus hanging on the boundary.
 */
function getFocusPointBeyondTable(
  editor: Editor,
  options: { direction: 'down' | 'up'; tablePath: Path; unit: 'character' | 'line' }
): Point | undefined {
  const { direction, tablePath, unit } = options;
  const isForward = direction === 'down';

  const boundaryPoint = isForward
    ? Editor.after(editor, tablePath)
    : Editor.before(editor, tablePath);
  if (!boundaryPoint || unit === 'character') return boundaryPoint;

  const rMLineEntry = Queries.getRMLinesAboveCMPoint(
    editor,
    boundaryPoint,
    isForward ? 'forward' : 'backward'
  ).next().value;
  if (!rMLineEntry) return boundaryPoint;

  const [rMLine, rMLinePath] = rMLineEntry;
  const rMParaEntry = Queries.renderModelAbove(editor, {
    at: rMLinePath,
    match: isRenderModelParagraph,
  });
  if (!rMParaEntry) return boundaryPoint;

  const cMParaPath = Queries.getCMPathFromRMPath(editor, rMParaEntry[1]);
  const { end, start } = Queries.getLineCMPoints(cMParaPath, rMLine);

  return isForward ? end : start;
}

/** Return a copy of a candidate selection with its grid-selection props re-validated. */
function normalizeGridSelectionProps(editor: Editor, selection: AlineSelection): AlineSelection {
  return { ...selection, ...Queries.getNormalizedGridSelectionProps(editor, selection) };
}

/**
 * Get the parts of two ranges that are unique to each range. In terms of set theory, this returns
 * the relative complements of the two input ranges.
 */
function excludeRanges(range: Range, other: Range): [Range[], Range[]] {
  const uniqueToRange: Range[] = [];
  const uniqueToOther: Range[] = [];

  const [rangeStart, rangeEnd] = Range.edges(range);
  const [otherStart, otherEnd] = Range.edges(other);

  const betweenStarts = { anchor: rangeStart, focus: otherStart };
  const partOfRangeBeforeOther = Range.intersection(range, betweenStarts);
  const partOfOtherBeforeRange = Range.intersection(other, betweenStarts);

  if (partOfRangeBeforeOther && Range.isExpanded(partOfRangeBeforeOther))
    uniqueToRange.push(partOfRangeBeforeOther);

  if (partOfOtherBeforeRange && Range.isExpanded(partOfOtherBeforeRange))
    uniqueToOther.push(partOfOtherBeforeRange);

  const betweenEnds = { anchor: rangeEnd, focus: otherEnd };
  const partOfRangeAfterOther = Range.intersection(range, betweenEnds);
  const partOfOtherAfterRange = Range.intersection(other, betweenEnds);

  if (partOfRangeAfterOther && Range.isExpanded(partOfRangeAfterOther))
    uniqueToRange.push(partOfRangeAfterOther);

  if (partOfOtherAfterRange && Range.isExpanded(partOfOtherAfterRange))
    uniqueToOther.push(partOfOtherAfterRange);

  return [uniqueToRange, uniqueToOther] as const;
}

export default SelectionTransforms;
